/**
 * Tous les textes que l'APPLICATION possède, à un seul endroit.
 *
 * Ceux que l'école écrit elle-même (titre de page, intro, message de confirmation)
 * vivent dans sa marque, pas ici. Ici, c'est ce qui ne change jamais.
 *
 * Trois règles tenues dans ce fichier :
 *
 * 1. **Le mot est « demande », partout.** Il fonctionne dans toutes les positions :
 *    déposer une demande, votre demande, mes demandes, la demande a été reçue.
 *    « Besoin » ne survit pas à « mes besoins ». Une école qui préfère « offre »
 *    la nomme dans son titre de page, c'est prévu.
 *
 * 2. **On n'explique pas la mécanique au-dessus du champ, on la met dans le
 *    bouton.** « Recevoir un code par mail » dit ce qu'un paragraphe disait.
 *
 * 3. **On ne promet que ce qu'on fait.** Le texte précédent annonçait un lien de
 *    suivi envoyé par mail : aucun lien n'est envoyé, l'entreprise revient
 *    d'elle-même avec son email.
 */

export const copy = {
	deposit: {
		defaultTitle: "Déposer une demande de recrutement",
		eyebrow: "Espace partenaire",
		companySection: "Votre entreprise",
		requestSection: "Votre demande",
		companyName: "Nom de l'entreprise",
		contactName: "Votre nom",
		contactEmail: "Votre email",
		contactEmailHint: "C'est avec cette adresse que vous suivrez votre demande.",
		submit: "Envoyer ma demande",
		submitting: "Envoi…",
		noAccount: "Aucun compte à créer.",
		sendFailed: "L'envoi n'a pas abouti. Réessayez dans un instant.",
		sendFailedOffline: "L'envoi n'a pas abouti. Vérifiez votre connexion.",
		sentTitle: "C'est envoyé",
		sentFallback: (school: string) => `${school} a bien reçu votre demande.`,
		trackCta: "Suivre ma demande",
		backToEspace: "Revenir à mes offres",
		alreadyDeposited: "Déjà déposé une demande ?",
		alreadyDepositedLink: "Retrouvez-les ici",
		contactTitle: "Votre interlocuteur",
		unknownTitle: "Page introuvable",
		unknownBody:
			"Ce lien ne correspond à aucune école. Vérifiez l'adresse auprès de votre contact.",
		loading: "Chargement…",
		required: "obligatoire",
		// Dans une iframe, c'est l'école qui a collé le code d'intégration qui doit
		// comprendre. Le message s'adresse donc à elle, pas au visiteur.
		unknownEmbed: "Formulaire indisponible : aucune école ne correspond à ce lien.",
		// « Le lien est faux » et « je n'ai pas pu joindre le serveur » ne se disent pas
		// pareil : le premier envoie vérifier l'adresse, le second demande de réessayer.
		// Les confondre faisait chercher une faute de frappe dans une URL correcte.
		loadFailedTitle: "Page indisponible",
		loadFailedBody: "Nous n'avons pas pu charger cette page. Rechargez-la dans un instant.",
		loadFailedEmbed: "Formulaire momentanément indisponible. Rechargez la page.",
		retry: "Recharger la page",
	},

	/**
	 * Ce champ ne demande rien d'administratif : il demande un nom, et rend une fiche.
	 * Le mot « SIRET » a disparu de l'interface, il n'était utile qu'à nous.
	 */
	company: {
		label: "Votre entreprise",
		placeholder: "Nom ou numéro SIREN",
		hint: "Tapez les premières lettres, on retrouve le reste.",
		siren: "SIREN",
		noResult: "Aucune entreprise à ce nom.",
		closed: "Fermée",
		change: "Changer",
		manualCta: "Mon entreprise n'est pas en France",
		backToSearch: "Chercher dans l'annuaire",
		unavailable: "L'annuaire ne répond pas.",
	},

	paste: {
		title: "Vous avez déjà une annonce ?",
		intro: "Collez-la, on en tire les informations. Vous vérifierez juste après.",
		tabText: "Coller le texte",
		tabLink: "Coller un lien",
		placeholder:
			"Collez ici votre annonce : l'intitulé du poste, les missions, la ville, le type de contrat…",
		submit: "Lire mon annonce",
		reading: "Lecture…",
		pdfCta: "Déposer un PDF",
		manualCta: "Je n'ai pas d'annonce, je saisis à la main",
		failed: "La lecture n'a pas abouti. Vous pouvez saisir les champs à la main.",
		// On dit ce qui a été lu ET ce qui manque : taire les trous ferait déposer une
		// annonce incomplète sans que l'entreprise s'en aperçoive.
		reviewTitle: "Vérifiez ce qu'on a lu",
		reviewFilled: (n: number) =>
			n === 1 ? "1 champ rempli automatiquement." : `${n} champs remplis automatiquement.`,
		reviewMissing: (n: number) =>
			n === 1 ? "1 champ n'a pas été trouvé." : `${n} champs n'ont pas été trouvés.`,
		reviewBack: "Recommencer avec une autre annonce",
	},

	tracking: {
		eyebrow: "Espace partenaire",
		title: "Mes demandes",
		// Une seule phrase, et elle répond à la seule hésitation réelle : quel email ?
		emailHelp: "Indiquez l'adresse utilisée lors du dépôt.",
		emailLabel: "Votre email",
		emailPlaceholder: "prenom.nom@entreprise.fr",
		askCode: "Recevoir un code par mail",
		codeLabel: "Code à 6 chiffres",
		// On annonce l'envoi sans jamais dire si l'adresse est CONNUE : la réponse est
		// la même dans les deux cas, sinon ce formulaire dirait quelles entreprises
		// travaillent avec quelle école.
		//
		// En revanche l'adresse s'écrit en ENTIER, parce qu'elle vient d'être tapée
		// juste au-dessus. La masquer ne la cachait à personne, et empêchait de voir
		// la faute de frappe qu'on cherche précisément quand on n'a rien reçu.
		// (La porte du partage, elle, garde son masque : là, l'adresse vient du
		// partage, et un lien transféré ne doit rien apprendre.)
		codeSent: (email: string) => `Code envoyé à ${email}`,
		// La phrase renvoie à un geste qui existe sur l'écran : « redemandez un code »
		// ne désignait aucun bouton, il fallait deviner qu'il passait par « Changer
		// d'adresse ».
		wrongAddress: "Rien reçu ? Vérifiez l'adresse ou renvoyez le code.",
		changeEmail: "Changer d'adresse",
		resend: "Renvoyer le code",
		resent: (email: string) => `Nouveau code envoyé à ${email}`,
		// Le serveur refuse un second code avant une minute, sans le dire (sa réponse ne
		// révèle jamais si un mail est parti). Annoncer « nouveau code envoyé » pendant
		// cette minute serait faux : l'écran le dit avant de demander.
		resendTooSoon: "Attendez une minute avant de redemander un code.",
		codeIncomplete: "Le code compte 6 chiffres.",
		openList: "Voir mes demandes",
		listSubtitle: (n: number) => (n === 1 ? "1 demande déposée" : `${n} demandes déposées`),
		emptyTitle: "Aucune demande pour l'instant",
		emptyBody: "Déposez-en une depuis la page de votre école, puis revenez ici.",
		depositedOn: (date: string) => `Déposée le ${date}`,
		codeRejected: "Ce code n'est pas le bon.",
		throttled: "Trop de tentatives. Réessayez dans une minute.",
		networkFailed: "La connexion a échoué. Vérifiez votre réseau et réessayez.",
	},

	share: {
		eyebrow: "Profils partagés",
		title: "Accéder aux profils",
		codeSent: (masked: string) => `Code envoyé à ${masked}`,
		codeLabel: "Code à 6 chiffres",
		submit: "Accéder",
		checking: "Vérification du lien…",
		noAccessTitle: "Aucun accès ouvert",
		noAccessBody: "Ouvrez le lien reçu par mail pour accéder aux profils partagés.",
		sharedBy: (school: string) => `Profils partagés par ${school}`,
		failed: "La connexion a échoué. Rechargez la page pour réessayer.",
	},

	/** Ce que voit l'entreprise, pas le vocabulaire interne de la base. */
	requestStatus: {
		nouveau: "Reçue",
		a_completer: "Complément demandé",
		validee: "Publiée",
		refusee: "Non retenue",
	} as Record<string, string>,
} as const;
